import React, { useEffect } from "react";

const LaboratoryResultPaper = ({ labTest, tests }) => {
  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "long",
    }).format(new Date(date));
  };

  const formatReferenceRange = (testType) => {
    if (!testType) return "";
    if (testType.reference_range) return testType.reference_range;
    if (testType.min_value != null && testType.max_value != null) {
      const range = `${testType.min_value} - ${testType.max_value}`;
      return testType.units ? `${range} ${testType.units}` : range;
    }
    return "";
  };

  useEffect(() => {
    const printStyles = `
      @media print {
        @page {
          size: A4;
          margin: 10mm 10mm 20mm 10mm;
        }
        body {
          font-family: 'Arial', sans-serif;
          margin: 0;
          padding: 0;
          color: #000;
        }
        .print-container {
          position: relative;
          min-height: 100vh;
        }
        .content-wrapper {
          padding-bottom: 180px; /* Space for footer */
        }
        table {
          width: 100%;
          page-break-inside: auto;
          font-size: 14px;
        }
        tr {
          page-break-inside: avoid;
          page-break-after: auto;
        }
        .test-section {
          page-break-inside: avoid;
          margin-bottom: 20px;
        }
        .footer {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 180px;
        }
        .avoid-break {
          page-break-inside: avoid;
        }
        h1, h2, h3 {
          margin: 0;
        }
      }
    `;
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = printStyles;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  if (!labTest || !tests || tests.length === 0) {
    return null;
  }

  // Separate CBC tests from others
  const cbcTests = tests.filter(
    (test) =>
      (test.test_type_display && test.test_type_display.name === "CBC") ||
      (test.parent_test && test.parent_test.name === "CBC")
  );
  const otherTests = tests.filter((test) => !cbcTests.includes(test));

  // Group CBC tests by category
  const cbcGroupedTests = cbcTests.reduce((acc, test) => {
    const category = test.test_type_display.category.name;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(test);
    return acc;
  }, {});

  const renderCBCTests = () => {
    if (cbcTests.length === 0) {
      return null;
    }

    return (
      <div className="test-section">
        <h2
          style={{
            textAlign: "center",
            fontSize: "18px",
            fontWeight: "bold",
            marginBottom: "20px",
            color: "#333",
            borderBottom: "2px solid #E91E63",
            paddingBottom: "8px",
          }}
        >
          Laboratory Test Results
        </h2>

        <table
          style={{
            width: "100%",
            marginBottom: "30px",
            borderCollapse: "collapse",
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: "#f5f5f5",
                textAlign: "left",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              <th style={{ padding: "10px", border: "1px solid #ddd" }}>
                Test Name
              </th>
              <th style={{ padding: "10px", border: "1px solid #ddd" }}>
                Result
              </th>
              <th style={{ padding: "10px", border: "1px solid #ddd" }}>
                Unit
              </th>
              <th style={{ padding: "10px", border: "1px solid #ddd" }}>
                Reference Range
              </th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(cbcGroupedTests).map((category, index) => (
              <React.Fragment key={index}>
                <tr>
                  <td
                    colSpan={4}
                    style={{
                      fontWeight: "bold",
                      fontSize: "16px",
                      padding: "12px",
                      textAlign: "center",
                      backgroundColor: "#eee",
                    }}
                  >
                    {category.toUpperCase()}
                  </td>
                </tr>

                {Object.entries(
                  cbcGroupedTests[category].reduce((parents, test) => {
                    const parentTest = test.parent_test
                      ? test.parent_test.name
                      : test.test_type_display.name;
                    if (!parents[parentTest]) {
                      parents[parentTest] = [];
                    }
                    if (
                      test.parent_test ||
                      test.test_type_display.name === "CBC"
                    ) {
                      parents[parentTest].push(test);
                    }
                    return parents;
                  }, {})
                ).map(([parentTest, childTests], parentIndex) => (
                  <React.Fragment key={parentIndex}>
                    <tr>
                      <td
                        colSpan={4}
                        style={{
                          fontWeight: "bold",
                          fontSize: "14px",
                          padding: "10px 12px",
                          textAlign: "left",
                          backgroundColor: "#f9f9f9",
                        }}
                      >
                        {parentTest}
                      </td>
                    </tr>

                    {childTests.map((childTest, childIndex) => (
                      <tr key={childIndex}>
                        <td
                          style={{
                            padding: "10px 24px",
                            fontSize: "14px",
                            border: "1px solid #ddd",
                          }}
                        >
                          {childTest.test_type_display.name}
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            fontSize: "14px",
                            border: "1px solid #ddd",
                            fontWeight: "bold",
                            color: "#000",
                          }}
                        >
                          {childTest.test_type_display.result_value}
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            fontSize: "14px",
                            border: "1px solid #ddd",
                          }}
                        >
                          {childTest.test_type_display.units}
                        </td>
                        <td
                          style={{
                            padding: "10px",
                            fontSize: "14px",
                            border: "1px solid #ddd",
                            whiteSpace: "pre-line",
                          }}
                        >
                          {formatReferenceRange(childTest.test_type_display)}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}

                {cbcTests
                  .filter(
                    (test) =>
                      !test.parent_test &&
                      test.test_type_display.name === "CBC" &&
                      test.test_type_display.result_value
                  )
                  .map((test, index) => (
                    <tr key={index}>
                      <td
                        style={{
                          padding: "10px 24px",
                          fontSize: "14px",
                          border: "1px solid #ddd",
                        }}
                      >
                        {test.test_type_display.name}
                      </td>
                      <td
                        style={{
                          padding: "10px",
                          fontSize: "14px",
                          border: "1px solid #ddd",
                          fontWeight: "bold",
                          color: "#000",
                        }}
                      >
                        {test.test_type_display.result_value}
                      </td>
                      <td
                        style={{
                          padding: "10px",
                          fontSize: "14px",
                          border: "1px solid #ddd",
                        }}
                      >
                        {test.test_type_display.units}
                      </td>
                      <td
                        style={{
                          padding: "10px",
                          fontSize: "14px",
                          border: "1px solid #ddd",
                          whiteSpace: "pre-line",
                        }}
                      >
                        {formatReferenceRange(test.test_type_display)}
                      </td>
                    </tr>
                  ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Group other tests by category first
  const otherTestsByCategory = otherTests.reduce((acc, test) => {
    const categoryName =
      (test.test_type_display.category &&
        test.test_type_display.category.name) ||
      "Uncategorized";
    if (!acc[categoryName]) acc[categoryName] = [];
    acc[categoryName].push(test);
    return acc;
  }, {});

  // Now group within each category by parent/child relationship
  const otherGroupedTests = Object.fromEntries(
    Object.entries(otherTestsByCategory).map(([catName, testsInCat]) => {
      const grouped = testsInCat.reduce((acc, test) => {
        const groupName = test.parent_test
          ? test.parent_test.name
          : test.test_type_display.name;

        if (!acc[groupName]) {
          acc[groupName] = {
            parent: null,
            children: [],
          };
        }

        if (test.child_tests && test.child_tests.length > 0) {
          acc[groupName].parent = test;
        } else if (test.parent_test) {
          acc[groupName].children.push(test);
        } else {
          // standalone test
          acc[groupName].parent = test;
        }

        return acc;
      }, {});
      return [catName, grouped];
    })
  );

  const renderOtherTests = () => {
    const categoryNames = Object.keys(otherGroupedTests);
    if (categoryNames.length === 0) return null;

    return (
      <div className="test-section">
        <h2
          style={{
            textAlign: "center",
            fontSize: "18px",
            fontWeight: "bold",
            marginBottom: "20px",
            color: "#333",
            borderBottom: "2px solid #E91E63",
            paddingBottom: "8px",
          }}
        >
          Laboratory Test Results - Other Tests
        </h2>

        <table
          style={{
            width: "100%",
            marginBottom: "30px",
            borderCollapse: "collapse",
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: "#f5f5f5",
                textAlign: "left",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              <th style={{ padding: "10px", border: "1px solid #ddd" }}>
                Test Name
              </th>
              <th style={{ padding: "10px", border: "1px solid #ddd" }}>
                Result
              </th>
              <th style={{ padding: "10px", border: "1px solid #ddd" }}>
                Unit
              </th>
              <th style={{ padding: "10px", border: "1px solid #ddd" }}>
                Reference Range
              </th>
            </tr>
          </thead>
          <tbody>
            {categoryNames.map((catName, catIndex) => {
              const groups = otherGroupedTests[catName];
              const groupKeys = Object.keys(groups);

              return (
                <React.Fragment key={catIndex}>
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        fontWeight: "bold",
                        fontSize: "16px",
                        padding: "12px",
                        textAlign: "center",
                        backgroundColor: "#eee",
                      }}
                    >
                      {catName.toUpperCase()}
                    </td>
                  </tr>

                  {groupKeys.map((groupName, index) => {
                    const { parent, children } = groups[groupName];
                    const displayedName = parent
                      ? parent.parent_test
                        ? parent.parent_test.name
                        : parent.test_type_display.name
                      : groupName;

                    return (
                      <React.Fragment key={index}>
                        <tr>
                          <td
                            colSpan={4}
                            style={{
                              fontWeight: "bold",
                              fontSize: "14px",
                              padding: "10px 12px",
                              textAlign: "left",
                              backgroundColor: "#f9f9f9",
                            }}
                          >
                            {displayedName}
                          </td>
                        </tr>

                        {children && children.length > 0
                          ? children.map((childTest, cIndex) => (
                              <tr key={cIndex}>
                                <td
                                  style={{
                                    padding: "10px 24px",
                                    fontSize: "14px",
                                    border: "1px solid #ddd",
                                  }}
                                >
                                  {childTest.test_type_display.name}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    fontSize: "14px",
                                    border: "1px solid #ddd",
                                    fontWeight: "bold",
                                    color: "#000",
                                  }}
                                >
                                  {childTest.test_type_display.result_value}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    fontSize: "14px",
                                    border: "1px solid #ddd",
                                  }}
                                >
                                  {childTest.test_type_display.units}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    fontSize: "14px",
                                    border: "1px solid #ddd",
                                    whiteSpace: "pre-line",
                                  }}
                                >
                                  {formatReferenceRange(
                                    childTest.test_type_display,
                                  )}
                                </td>
                              </tr>
                            ))
                          : parent &&
                            parent.child_tests &&
                            parent.child_tests.length > 0
                          ? parent.child_tests.map((childTest, cIndex) => (
                              <tr key={cIndex}>
                                <td
                                  style={{
                                    padding: "10px 24px",
                                    fontSize: "14px",
                                    border: "1px solid #ddd",
                                  }}
                                >
                                  {childTest.name}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    fontSize: "14px",
                                    border: "1px solid #ddd",
                                    fontWeight: "bold",
                                    color: "#000",
                                  }}
                                >
                                  {childTest.result_value}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    fontSize: "14px",
                                    border: "1px solid #ddd",
                                  }}
                                >
                                  {childTest.units}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    fontSize: "14px",
                                    border: "1px solid #ddd",
                                    whiteSpace: "pre-line",
                                  }}
                                >
                                  {formatReferenceRange(childTest)}
                                </td>
                              </tr>
                            ))
                          : parent && (
                              <tr>
                                <td
                                  style={{
                                    padding: "10px 24px",
                                    fontSize: "14px",
                                    border: "1px solid #ddd",
                                  }}
                                >
                                  {parent.test_type_display.name}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    fontSize: "14px",
                                    border: "1px solid #ddd",
                                    fontWeight: "bold",
                                    color: "#000",
                                  }}
                                >
                                  {parent.test_type_display.result_value}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    fontSize: "14px",
                                    border: "1px solid #ddd",
                                  }}
                                >
                                  {parent.test_type_display.units}
                                </td>
                                <td
                                  style={{
                                    padding: "10px",
                                    fontSize: "14px",
                                    border: "1px solid #ddd",
                                    whiteSpace: "pre-line",
                                  }}
                                >
                                  {formatReferenceRange(parent.test_type_display)}
                                </td>
                              </tr>
                            )}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="print-container">
      <div className="content-wrapper" style={{ minHeight: "85vh" }}>
        {/* Header Section */}
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: "15px",
            marginBottom: "20px",
            borderBottom: "2px solid #E91E63",
            direction: "ltr",
          }}
        >
          <div style={{ textAlign: "left" }}>
            <h1
              style={{
                fontSize: "28px",
                fontWeight: "bold",
                margin: 0,
                color: "#000",
              }}
            >
              TURKISH CLINIC
            </h1>
            <p
              style={{
                fontSize: "16px",
                margin: "5px 0",
                color: "#E91E63",
                fontWeight: "600",
              }}
            >
              GAIN YOUR BEAUTY
            </p>
          </div>

          <div style={{ textAlign: "center" }}>
            <img
              src={`${process.env.REACT_APP_MEDIA_URL}/images/logo/logo.png`}
              alt="Clinic Logo"
              style={{ width: "100px", height: "auto", marginBottom: "5px" }}
            />
          </div>

          <div style={{ textAlign: "right", direction: "rtl" }}>
            <h2
              style={{
                fontSize: "28px",
                fontWeight: "bold",
                margin: 0,
                color: "#E91E63",
              }}
            >
              ترکیش کلینیک
            </h2>
            <p
              style={{
                fontSize: "16px",
                margin: "5px 0",
                color: "#333",
              }}
            >
              زیبایی خود را به دست آورید
            </p>
          </div>
        </header>

        {/* Patient Information */}
        <div style={{ marginBottom: "25px" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <p style={{ fontSize: "16px", margin: "8px 0" }}>
                <strong style={{ fontSize: "16px" }}>Patient ID:</strong>{" "}
                <span style={{ fontWeight: "bold" }}>
                  {labTest.patient_display.id || "N/A"}
                </span>
              </p>
              <p style={{ fontSize: "16px", margin: "8px 0" }}>
                <strong style={{ fontSize: "16px" }}>Patient Name:</strong>{" "}
                <span style={{ fontWeight: "bold" }}>
                  {labTest.patient_display.name || "N/A"}
                </span>
              </p>
              <p style={{ fontSize: "16px", margin: "8px 0" }}>
                <strong style={{ fontSize: "16px" }}>Age/Sex:</strong>{" "}
                <span style={{ fontWeight: "bold" }}>
                  {labTest.patient_display.age || "N/A"}{" "}
                  {labTest.patient_display.gender || "N/A"}
                </span>
              </p>
            </div>
            <div>
              <p style={{ fontSize: "16px", margin: "8px 0" }}>
                <strong style={{ fontSize: "16px" }}>Date:</strong>{" "}
                <span style={{ fontWeight: "bold" }}>
                  {formatDate(labTest.order_date)}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Render Other Tests */}
        {renderOtherTests()}

        {/* Render CBC Tests */}
        {renderCBCTests()}
      </div>

      {/* Footer Section - Will appear only at the end of the document */}
      <div className="footer avoid-break">
        <div
          style={{
            backgroundColor: "#000",
            color: "#fff",
            padding: "12px",
            fontWeight: "bold",
            fontSize: "14px",
            textAlign: "center",
          }}
        >
          سرک سوم قلعه فتح الله، ناحیه دهم کابل.
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#fff",
            padding: "20px",
            borderTop: "3px solid #000",
          }}
        >
          <div style={{ textAlign: "left" }}>
            <p style={{ margin: "0", fontSize: "14px", fontWeight: "bold" }}>
              📞 +93 (0) 782 57 57 57
            </p>
            <p style={{ margin: "0", fontSize: "14px", fontWeight: "bold" }}>
              📞 +93 (0) 789 20 90 54
            </p>
          </div>

          <div style={{ textAlign: "center" }}>
            <p style={{ margin: "0", fontSize: "14px", fontWeight: "bold" }}>
              📧 info@turkishclinic.af
            </p>
            <p style={{ margin: "0", fontSize: "14px", fontWeight: "bold" }}>
              🌐 www.turkishclinic.af
            </p>
          </div>

          <div style={{ textAlign: "right" }}>
            <p style={{ margin: "0", fontSize: "14px", fontWeight: "bold" }}>
              📱 +93 (0) 782 57 57 57
            </p>
            <p style={{ margin: "0", fontSize: "14px", fontWeight: "bold" }}>
              📱 +93 (0) 789 20 90 54
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LaboratoryResultPaper;
